import React, { useEffect, useState } from 'react';
import customFetch from '../utils/customFetch';
import { redirect, useLoaderData, useNavigate } from 'react-router-dom';
import {
  Banner,
  Logo,
  ProfileDownloader,
  PublicLinkItem,
  QrCodePopup,
} from '../components';
import { trackPageView, trackQRView } from '../utils/analytics';
import {
  patternStyle,
  DEFAULT_PATTERN_COLOR,
  DEFAULT_PATTERN_OPACITY,
} from '../utils/heroPatterns';
import { Helmet } from 'react-helmet';
import EmptyLinks from '../components/linksPublic/emptyLinks';
import BlockedPage from '../components/linksPublic/blockedPage';
import { QrCodeIcon } from '@heroicons/react/20/solid';
import { buildCloudinaryUrl } from '../utils/buildCloudinaryUrl';

export const loader = async ({ params }) => {
  try {
    const { data } = await customFetch.get(`/public/links/${params.id}`);
    return data;
  } catch (error) {
    return redirect('/');
  }
};

const CardPage = () => {
  const data = useLoaderData();
  // Wenn noch keine Settings gespeichert wurden, werden hier defaults festgelegt
  if (!data.linkSettings) {
    data.linkSettings = {
      backgroundContent: 'Topografie',
      backgroundType: 'Muster',
      iconColor: '#2a2a2a',
      linkColor: '#2a2a2a',
      patternColor: DEFAULT_PATTERN_COLOR,
      patternOpacity: DEFAULT_PATTERN_OPACITY,
    };
  }
  const patternBackground = patternStyle({
    key: data.linkSettings.backgroundContent,
    color: data.linkSettings.patternColor ?? DEFAULT_PATTERN_COLOR,
    opacity: data.linkSettings.patternOpacity ?? DEFAULT_PATTERN_OPACITY,
  });

  // QR Code Popup State
  const [openQrPopup, setOpenQrPopup] = useState(false);

  // Wenn Nutzer in localStorage gleich dem Seiteninhaber, zeige Seite an. Sonst redirect
  const userObject = localStorage.getItem('user');
  const user = userObject ? JSON.parse(userObject) : { url: 'test' };
  const navigate = useNavigate();
  useEffect(() => {
    if (user && data.user.status === 'pending' && data.user.url !== user.url) {
      navigate('/404');
    }
  }, []);

  // Track page view
  useEffect(() => {
    if (data.linkSettings.owner) {
      trackPageView(data.linkSettings.owner);
    }
  }, [data.linkSettings.owner]);

  // S2/D2: der Server liefert die fertige Auslieferungs-URL (avatarUrl,
  // vCardAvatarUrl, headerUrl) und entscheidet dabei per StorageKey zwischen
  // SOS und Cloudinary. Der buildCloudinaryUrl-Zweig ist nur noch der
  // Fallback für Antworten ohne diese Felder; er fliegt mit S4 raus.
  const optimizedAvatar =
    data.user.avatarUrl ||
    buildCloudinaryUrl(
      data.user.avatarPublicId || 'Default_user_pic_ncdhdy',
      'w_800',
    );

  const vCardAvatar =
    data.user.vCardAvatarUrl ||
    buildCloudinaryUrl(
      data.user.avatarPublicId || 'Default_user_pic_ncdhdy',
      'w_200,h_200,c_fill,q_80,f_jpg',
    );

  const optimizedHeader =
    data.linkSettings.headerUrl ||
    buildCloudinaryUrl(
      data.linkSettings.headerContentPublicId || 'background_c4jgy3.jpg',
      'w_1200',
    );

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      {/* Hier Unterscheidung, ob Profil blockiert */}

      {data.user.status === 'blocked' ? (
        <BlockedPage />
      ) : (
        <>
          <div
            style={
              data.linkSettings.backgroundType === 'Muster'
                ? patternBackground
                : data.linkSettings.backgroundType === 'Farbe'
                  ? {
                      backgroundColor: data.linkSettings.backgroundContent,
                    }
                  : {}
            }
            className="fixed  top-0 w-full h-full -z-10"
          ></div>

          {/* Pending Page Logik Start */}

          {data.user.status === 'pending' && data.user.url === user.url ? (
            <Banner
              header="Achtung, diese Seite ist nur eine Vorschau"
              content="Deine Seite ist erst öffentlich zugänglich, wenn du deine erste sVisit-Karte gekauft hast und deine Bestellung bearbeitet wurde."
              link="/produkte"
            />
          ) : (
            ''
          )}

          {/* Pending Page Logik Ende */}

          {/* Full Page QR Code Popup Start */}
          <QrCodePopup
            openQrPopup={openQrPopup}
            setOpenQrPopup={setOpenQrPopup}
            url={data.user.url}
          />
          {/* Full Page QR Code Popup Ende */}

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl">
              {/* Header und Profilbild Start */}
              <div className="block w-full mt-10">
                {data.linkSettings.showHeader ? (
                  <>
                    <div className="aspect-w-2 aspect-h-1 rounded-lg overflow-hidden shadow-2xl">
                      <img
                        src={optimizedHeader}
                        alt="header"
                        className="object-cover"
                      />
                    </div>
                    <img
                      className="sm:h-80 sm:w-80 h-52 w-52 rounded-full mx-auto shadow-2xl object-cover sm:-mt-40 -mt-24 relative"
                      src={optimizedAvatar}
                      alt="avatar"
                    />
                  </>
                ) : (
                  <img
                    className="sm:h-80 sm:w-80 h-60 w-60 rounded-full mx-auto shadow-2xl object-cover"
                    src={optimizedAvatar}
                    alt="avatar"
                  />
                )}
              </div>
              {/* Header und Profilbild Ende */}
              {/* Name und Titel des Nutzers Start */}
              <div
                className="text-center mt-10"
                style={{ color: data.linkSettings.linkColor }}
              >
                <h1 className="capitalize text-3xl font-semibold sm:text-4xl">{`${data.user.firstName} ${data.user.lastName}`}</h1>
                <h2 className="mt-4 text-xl font-semibold sm:text-2xl">
                  {`${data.user.title ? data.user.title : ''}${
                    data.user.title && data.user.company ? ' bei ' : ''
                  }${data.user.company ? data.user.company : ''}`}
                </h2>
              </div>
              {/* Name und Titel des Nutzers Ende */}
              {/* Profil Download und QR-Code Start */}
              <div className="mt-4 mb-10 flex justify-center gap-5">
                {/* Profile Downloader alle Angaben zum Nutzer übergeben, damit VCard erstellt werden kann*/}
                <ProfileDownloader
                  textColor={data.linkSettings.linkColor}
                  user={data.user}
                  optimizedAvatar={vCardAvatar}
                  links={data.links}
                  ownerId={data.linkSettings.owner}
                />
                <button
                  type="button"
                  className="rounded-md bg-white px-3.5 py-2.5 text-lg font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 shadow-lg hover:shadow-sm transition-all"
                  style={{ color: data.linkSettings.linkColor }}
                  onClick={() => {
                    setOpenQrPopup(true);
                    trackQRView(data.linkSettings.owner);
                  }}
                >
                  <QrCodeIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              {/* Profil Download und QR-Code Ende */}
              <ul className="w-[100%]">
                {Object.keys(data.links).length > 0 ? (
                  data.links.map((link) => (
                    <PublicLinkItem
                      {...link}
                      key={link._id}
                      userId={data.linkSettings.owner}
                      linkColor={data.linkSettings.linkColor}
                      iconColor={data.linkSettings.iconColor}
                    />
                  ))
                ) : (
                  <EmptyLinks />
                )}
              </ul>
            </div>
          </div>
          <footer>
            <div className="mx-auto max-w-7xl overflow-hidden px-6 pt-20 pb-5 sm:pt-24 lg:px-8">
              <div className="mt-10 justify-center">
                <a href="/" target="_blank">
                  <Logo />
                  <h3 className="text-center text-sm font-semibold leading-6 text-gray-900 mt-4">
                    sVisit
                  </h3>
                </a>
              </div>
              <p className="mt-10 text-center text-xs leading-5 text-gray-500">
                &copy; {new Date().getFullYear()} sVisit.
              </p>
            </div>
          </footer>
        </>
      )}
    </>
  );
};

export default CardPage;
